// ---------------------------------------------------------------------------
// LlmOverviewSections — the Model-configuration and Invocation cards for an
// LLM-judge grader. Editable: reads values/handlers from the Overview edit
// context so the same markup serves both view (disabled) and edit modes.
// Extracted from OverviewTab so the grader-type registry owns this markup.
// ---------------------------------------------------------------------------

import { Field, Input, Textarea } from '@fluentui/react-components';

import { ExpandableTextarea } from '../../../../components/ExpandableTextarea/index.js';
import { useStyles } from '../../GraderPage.style.js';
import type { OverviewSectionsProps } from '../graderType.types.js';

export function LlmOverviewSections({ edit }: OverviewSectionsProps) {
  const styles = useStyles();
  const {
    viewOnly,
    disabled,
    modelName,
    onModelNameChange,
    systemPrompt,
    onSystemPromptChange,
    temperature,
    onTemperatureChange,
    topP,
    onTopPChange,
    maxTokens,
    onMaxTokensChange,
    userMessage,
    onUserMessageChange,
  } = edit;

  return (
    <>
      <section className={styles.overviewCard} aria-labelledby="grader-model">
        <div id="grader-model" className={styles.overviewCardTitle}>
          Model configuration
          <span className={styles.overviewCardTitleHelp}>
            Creation-time settings, fixed across all invocations.
          </span>
        </div>
        <div className={styles.overviewCardFields}>
          <Field label="Model name" required>
            <Input
              className={styles.fullWidthControl}
              value={modelName}
              placeholder="No model configured"
              disabled={viewOnly || disabled}
              onChange={(_, data) => onModelNameChange?.(data.value)}
            />
          </Field>
          <ExpandableTextarea
            label="System prompt"
            required
            value={systemPrompt}
            onChange={(value) => onSystemPromptChange?.(value)}
            rows={4}
            disabled={viewOnly || disabled}
            placeholder="You are an expert evaluator..."
          />
          <div className={styles.overviewFieldRow}>
            <Field
              className={styles.overviewFieldColumn}
              label="Temperature"
              hint="0.0–2.0"
            >
              <Input
                className={styles.fullWidthControl}
                value={temperature}
                placeholder="default"
                disabled={viewOnly || disabled}
                onChange={(_, data) => onTemperatureChange?.(data.value)}
              />
            </Field>
            <Field
              className={styles.overviewFieldColumn}
              label="Top P"
              hint="0.0–1.0"
            >
              <Input
                className={styles.fullWidthControl}
                value={topP}
                placeholder="default"
                disabled={viewOnly || disabled}
                onChange={(_, data) => onTopPChange?.(data.value)}
              />
            </Field>
            <Field className={styles.overviewFieldColumn} label="Max tokens">
              <Input
                className={styles.fullWidthControl}
                value={maxTokens}
                placeholder="default"
                disabled={viewOnly || disabled}
                onChange={(_, data) => onMaxTokensChange?.(data.value)}
              />
            </Field>
          </div>
        </div>
      </section>

      <section
        className={styles.overviewCard}
        aria-labelledby="grader-invocation"
      >
        <div id="grader-invocation" className={styles.overviewCardTitle}>
          Invocation
          <span className={styles.overviewCardTitleHelp}>
            Per-execution settings, resolved fresh for each trial.
          </span>
        </div>
        <div className={styles.overviewCardFields}>
          <Field
            label="User message"
            hint="Template applied per trial. May reference declared variables."
          >
            <Textarea
              className={styles.fullWidthControl}
              value={userMessage}
              onChange={(_, data) => onUserMessageChange?.(data.value)}
              rows={3}
              placeholder="{{$var:USER_QUERY}}"
              disabled={viewOnly || disabled}
            />
          </Field>
        </div>
      </section>
    </>
  );
}
